/**
 * Lanternfall — the between-raids hub (M2-DESIGN §5).
 *
 * The host owns the canvas and the input locks; this module owns everything inside
 * `#hub`. The two talk through `HubHost` (hub → host) and `HubHandle` (host → hub) and
 * nothing else, so the hub can be shown over a live scene, over a frozen still, or over
 * nothing at all without knowing which.
 */

import { h, render } from 'preact';
import type { FeedbackCollector } from '../../game/feedback.js';
import type { FrameCapture } from '../../render/capture.js';
import { progressStore } from '../../net/progress-store.js';
import { session } from '../../net/session.js';
import { App } from './App.js';
import { HubController } from './common.js';
import { Waking } from './Waking.js';
import './hub.css';

export interface HubDeployRequest {
  /**
   * `'tutorial'` is The Waking: a private, solo room on the Yard with the director off. It never
   * comes from the Deploy tab — only the WAKE button and, once, the retry after an auth kick.
   */
  mode: 'raid' | 'expedition' | 'tutorial';
  mapId: string;
  /** Opaque squad hint: a friend code, a squad name, or both. Squads land in a later milestone. */
  squad: string;
}

export interface HubHost {
  collector: FeedbackCollector;
  capture: FrameCapture;
  /** Player pressed Deploy. The host connects to the server and hides the hub. */
  onDeploy: (req: HubDeployRequest) => void;
  /** Any setting changed; the host re-applies sensitivity/FOV/touch scale live. */
  onSettingsChanged: () => void;
  /** Hub wants pointer/keyboard released to the page (called on show) or returned (on hide). */
  onVisibilityChanged: (visible: boolean) => void;
}

export interface HubHandle {
  show(): void;
  hide(): void;
  readonly visible: boolean;
  /** ESC pressed. Returns true if the hub consumed it (closed a modal/panel). */
  escape(): boolean;
  /** Re-fetch the tab data (called after a raid). */
  refresh(): void;
  /** Jump straight to the feedback terminal (F8 from the hub). */
  openTerminal(): void;
  /** Open with the card that introduces the town — the one moment after The Waking. */
  announceFirstLight(): void;
}

export function mountHub(root: HTMLElement, host: HubHost): HubHandle {
  const ctl = new HubController();
  render(h(App, { host, ctl }), root);

  let visible = false;

  const setVisible = (next: boolean): void => {
    if (visible === next) return;
    visible = next;
    root.classList.toggle('on', next);
    // The host paints a blurred still of the last frame into #hubbg; we only own its
    // visibility so the hub never has to know whether a frame was ever rendered.
    document.getElementById('hubbg')?.classList.toggle('on', next);
    ctl.setVisible(next);
    // Scroll position survives a hide/show, which is right for the stash and wrong for
    // nothing, so it is left alone.
    host.onVisibilityChanged(next);
  };

  return {
    show(): void {
      setVisible(true);
    },
    hide(): void {
      setVisible(false);
    },
    get visible(): boolean {
      return visible;
    },
    escape(): boolean {
      return ctl.escape();
    },
    refresh(): void {
      // XP, skill points, contract progress and Marks all moved while the player was out;
      // drop the shared progress cache so the next read really goes to the server.
      progressStore.invalidate();
      ctl.refresh();
    },
    openTerminal(): void {
      ctl.setTab('terminal');
      setVisible(true);
    },
    announceFirstLight(): void {
      ctl.announceFirstLight();
    },
  };
}

// ----------------------------------------------------------------- The Waking

/**
 * The Waking — the screen a brand-new Waker sees instead of Lanternfall.
 *
 * Mounted on its own root (`#waking`) rather than inside the hub shell, because it is not a tab
 * and must never inherit the hub's header, briefing rail or Town strip: the whole point of the
 * screen is that there are three questions on it and nothing else. It is shown *instead of* the
 * hub, never over it, so the two are never both visible.
 */
export interface WakingHost {
  /** WAKE. The three choices are already persisted; the host deploys straight into the tutorial. */
  onWake: (chosen: { name: string; race: number; calling: number }) => void;
}

export interface WakingHandle {
  show(): void;
  hide(): void;
  readonly visible: boolean;
}

export function mountWaking(root: HTMLElement, host: WakingHost): WakingHandle {
  let visible = false;

  const set = (next: boolean): void => {
    if (visible === next) return;
    visible = next;
    root.classList.toggle('on', next);
    // Mounted on show and **unmounted on hide**, unlike the hub's tabs.
    //
    // It used to be rendered once and left mounted, which was right when the screen was four
    // photographs. It now holds a live WebGPU device (`waking-preview.ts`), and a device left
    // running behind a hidden screen for the rest of the session is a second adapter the game's
    // own renderer is sharing a card with. Unmounting runs the component's effect cleanup,
    // which disposes it. Re-rendering on a later show costs nothing: this screen is seen once.
    render(next ? h(Waking, { token: session.token, onWake: host.onWake }) : null, root);
  };

  return {
    show(): void {
      set(true);
    },
    hide(): void {
      set(false);
    },
    get visible(): boolean {
      return visible;
    },
  };
}
